import { EmptyValueFallbackContext } from '@Core/components/dynamicValues/EmptyValueFallbackContext';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type KeyValueItem = {
  /**
   * Row label.
   */
  label: React.ReactNode;

  /**
   * Row value.
   */
  value: React.ReactNode;

  /**
   * Optional supporting text shown below the value.
   */
  description?: React.ReactNode;
};

type KeyValueListProps = {
  /**
   * Rows of labeled values.
   */
  items: KeyValueItem[];

  /**
   * Rendered in place of a row's value when it is empty (`null`, `undefined`, or an
   * empty string). `FormFieldDisplay` values inherit it too, unless they set their own
   * `fallback`. Omit to leave empty raw values blank and keep the em dash in field
   * displays. The fallback renders exactly as given — the caller owns its styling, so
   * pass a muted node (e.g. `<em>Not set</em>` or a `text-muted-foreground` span) if it
   * should read as a placeholder rather than a value.
   */
  fallback?: React.ReactNode;

  className?: string;
};

function isEmptyValue(value: React.ReactNode): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * A responsive definition list for record attributes and summary details. Use for detail
 * panels where labels and values should align cleanly.
 *
 * Prefer `FormFieldDisplay` over hand-formatted values for record fields — it renders
 * dates, selects, booleans, relations, and files from the resource schema.
 *
 * @example
 * <Sdk.Components.KeyValueList items={[{ label: 'Email', value: student.email }]} />
 *
 * @example
 * <Sdk.Components.KeyValueList
 *   fallback="?"
 *   items={[
 *     { label: 'Name', value: student.name },
 *     {
 *       label: 'Enrolled On',
 *       value: <Sdk.Components.FormFieldDisplay field={students.fields.enrolled_on} value={student.enrolled_on} />
 *     }
 *   ]}
 * />
 */
export function KeyValueList({ items, fallback, className }: KeyValueListProps) {
  const rows = (
    <dl className={cn('divide-y divide-border rounded-xl border border-border bg-card', className)}>
      {items.map((item, index) => (
        <div key={index} className="grid gap-1 p-4 sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm font-medium text-muted-foreground">{item.label}</dt>
          <dd className="sm:col-span-2">
            <div className="font-medium text-card-foreground">
              {fallback !== undefined && isEmptyValue(item.value) ? fallback : item.value}
            </div>
            {item.description ? (
              <div className="mt-1 text-sm text-muted-foreground">{item.description}</div>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );

  if (fallback === undefined) {
    return rows;
  }

  return (
    <EmptyValueFallbackContext.Provider value={fallback}>{rows}</EmptyValueFallbackContext.Provider>
  );
}
